import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { API, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Plus, TrashSimple, MagnifyingGlass, Copy, FunnelSimple, Bank, Eye, DownloadSimple, PencilSimple, Paperclip, Printer, LockKey } from "@phosphor-icons/react";
import { Modal, ModalFooter, Fld } from "./TCAndTCSR";
import { printFilterSummary } from "@/lib/printSummary";
import { MultiFilePicker } from "@/components/MultiFilePicker";

const STORAGE_KEY = "pi2_passcode"; // share the same unlock session with Purchase INV 2

function fmtDate(s){ if(!s) return "—"; if(/^\d{4}-\d{2}-\d{2}$/.test(s)) return s; const d=new Date(s); if(Number.isNaN(d.getTime())) return s; return d.toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"});}

export default function BankStatement() {
  const [passcode, setPasscode] = useState(() => sessionStorage.getItem(STORAGE_KEY) || "");
  const [input, setInput] = useState("");
  const [locked, setLocked] = useState(!sessionStorage.getItem(STORAGE_KEY));

  useEffect(() => {
    if (passcode) { sessionStorage.setItem(STORAGE_KEY, passcode); setLocked(false); }
  }, [passcode]);

  const submit = async (e) => {
    e?.preventDefault();
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bank-statements`, {
        headers: {
          "X-Passcode": input,
          Authorization: `Bearer ${localStorage.getItem("access_token") || ""}`,
        },
      });
      if (res.status === 403) { toast.error("Wrong passcode"); return; }
      setPasscode(input);
    } catch { toast.error("Could not verify passcode"); }
  };

  const relock = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPasscode(""); setInput(""); setLocked(true);
  };

  if (locked || !passcode) {
    return (
      <Layout>
        <div className="max-w-md mx-auto mt-20 bg-white border border-zinc-200 p-8" data-testid="bank-passcode-gate">
          <div className="flex items-center gap-2 mb-4">
            <LockKey size={22} weight="duotone" className="text-[#002FA7]"/>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono">Restricted</div>
              <div className="font-display font-black text-2xl text-zinc-950">Bank Statement</div>
            </div>
          </div>
          <p className="text-sm text-zinc-500 mb-4">This register is passcode-protected. Enter the passcode to view or add records.</p>
          <form onSubmit={submit} className="space-y-3">
            <input type="password" value={input} onChange={e=>setInput(e.target.value)}
              placeholder="Passcode" autoFocus data-testid="bank-passcode-input"
              className="w-full font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]"/>
            <button type="submit" data-testid="bank-passcode-submit"
              className="w-full bg-[#002FA7] text-white text-sm px-4 py-2 hover:bg-[#00227A]">Unlock</button>
          </form>
        </div>
      </Layout>
    );
  }

  return (
    <div className="relative">
      <BankStatementPage passcode={passcode}/>
      <button onClick={relock} data-testid="bank-relock" className="fixed bottom-4 right-4 bg-white border border-zinc-200 text-xs px-3 py-2 shadow-lg hover:bg-zinc-50 flex items-center gap-1">
        <LockKey size={12}/> Re-lock
      </button>
    </div>
  );
}

function BankStatementPage({ passcode }) {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(() => new Set((params.get("ids") || "").split(",").filter(Boolean)));

  const [q, setQ] = useState(params.get("q") || "");
  const [bankFilter, setBankFilter] = useState(params.get("bank") || "");
  const [from, setFrom] = useState(params.get("from") || "");
  const [to, setTo] = useState(params.get("to") || "");
  const [sortKey, setSortKey] = useState(params.get("sort") || "date");
  const [sortDir, setSortDir] = useState(params.get("dir") === "asc" ? "asc" : "desc");

  useEffect(() => {
    const n = {};
    if (q) n.q = q; if (bankFilter) n.bank = bankFilter;
    if (from) n.from = from; if (to) n.to = to;
    if (sortKey) { n.sort = sortKey; n.dir = sortDir; }
    if (selected.size) n.ids = Array.from(selected).join(",");
    setParams(n, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, bankFilter, from, to, sortKey, sortDir, selected]);

  const cfg = { headers: { "X-Passcode": passcode } };
  const load = async () => {
    setLoading(true);
    try {
      const [{ data: rows }, { data: bankList }] = await Promise.all([
        api.get("/bank-statements", cfg),
        api.get("/bank-names", cfg),
      ]);
      setItems(rows); setBanks(bankList);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [passcode]);

  const del = async (r) => {
    if (!window.confirm(`Delete ${r.bank_name} statement from ${fmtDate(r.date)}?`)) return;
    try {
      await api.delete(`/bank-statements/${r.id}`, cfg);
      setItems(xs => xs.filter(x => x.id !== r.id));
      setSelected(s => { const n = new Set(s); n.delete(r.id); return n; });
      toast.success("Deleted");
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const openFile = (id) => {
    const t = localStorage.getItem("access_token") || "";
    window.open(`${API}/bank-statements/${id}/download?auth=${encodeURIComponent(t)}&passcode=${encodeURIComponent(passcode)}`, "_blank");
  };
  const shareLink = async () => {
    const url = window.location.href;
    try { await navigator.clipboard.writeText(url); toast.success(selected.size ? `Link with ${selected.size} selected item(s) copied` : "Filtered link copied"); return; } catch {}
    try {
      const ta = document.createElement("textarea"); ta.value = url; ta.style.position="fixed"; ta.style.opacity="0";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      toast.success("Link copied");
    } catch { toast.error("Could not copy — copy the URL manually"); }
  };
  const clearF = () => { setQ(""); setBankFilter(""); setFrom(""); setTo(""); setSortKey("date"); setSortDir("desc"); };
  const toggleSort = (k) => {
    if (sortKey !== k) { setSortKey(k); setSortDir("asc"); }
    else if (sortDir === "asc") setSortDir("desc");
    else { setSortKey(null); setSortDir("asc"); }
  };

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    return items.filter(x => {
      if (n) { const hay = `${x.bank_name||""} ${x.description||""} ${x.filename||""}`.toLowerCase(); if (!hay.includes(n)) return false; }
      if (bankFilter && x.bank_name !== bankFilter) return false;
      if (from && (x.date||"") && x.date < from) return false;
      if (to   && (x.date||"") && x.date > to)   return false;
      return true;
    });
  }, [items, q, bankFilter, from, to]);

  const displayed = useMemo(() => {
    if (!sortKey) return filtered;
    const dir = sortDir === "asc" ? 1 : -1;
    const g = (x) => ({
      date: x.date||"",
      bank: (x.bank_name||"").toLowerCase(),
      description: (x.description||"").toLowerCase(),
    }[sortKey] ?? "");
    return [...filtered].sort((a,b)=>{ const va=g(a), vb=g(b); if (va<vb) return -dir; if (va>vb) return dir; return 0; });
  }, [filtered, sortKey, sortDir]);

  const selectedRows = useMemo(() => displayed.filter(r => selected.has(r.id)), [displayed, selected]);
  const allVisibleSelected = displayed.length > 0 && displayed.every(r => selected.has(r.id));

  const toggleAll = () => {
    setSelected(prev => {
      if (allVisibleSelected) {
        const n = new Set(prev);
        displayed.forEach(r => n.delete(r.id));
        return n;
      }
      const n = new Set(prev);
      displayed.forEach(r => n.add(r.id));
      return n;
    });
  };
  const toggleRow = (id) => setSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const clearSelection = () => setSelected(new Set());

  const bulkPreview = () => {
    const withFiles = selectedRows.filter(r => r.filename);
    if (!withFiles.length) { toast.error("None of the selected items have a file"); return; }
    if (withFiles.length > 8 && !window.confirm(`Open ${withFiles.length} files in new tabs?`)) return;
    withFiles.forEach((r, i) => setTimeout(() => openFile(r.id), i * 150));
  };

  const rowsForPrint = selectedRows.length ? selectedRows : displayed;
  const onPrint = () => {
    printFilterSummary({
      title: "Bank Statement — Filter Summary",
      filters: [
        selectedRows.length && `${selectedRows.length} selected item(s)`,
        q && `Search: "${q}"`,
        bankFilter && `Bank: ${bankFilter}`,
        from && `From: ${from}`,
        to && `To: ${to}`,
      ].filter(Boolean),
      columns: [
        { key: "date",        label: "Date",        format: fmtDate },
        { key: "bank_name",   label: "Bank" },
        { key: "description", label: "Description" },
        { key: "filename",    label: "File" },
      ],
      rows: rowsForPrint,
    });
  };

  const hasF = q || bankFilter || from || to;

  return (
    <Layout>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono mb-2">Accounts · Restricted</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight text-zinc-950">Bank Statement</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-xl">Passcode-protected register. Tick rows to preview, print, or share only the selected statements.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onPrint} data-testid="bank-print-btn" className="bg-white border border-zinc-200 text-zinc-800 text-sm px-3 py-2.5 flex items-center gap-2 hover:bg-zinc-50 transition-colors duration-150"><Printer size={14}/> Print{selectedRows.length?` (${selectedRows.length})`:" filter"}</button>
          <button onClick={shareLink} data-testid="bank-share-btn" className="bg-white border border-zinc-200 text-zinc-800 text-sm px-3 py-2.5 flex items-center gap-2 hover:bg-zinc-50 transition-colors duration-150"><Copy size={14}/> Share link</button>
          <button onClick={()=>setShowNew(true)} data-testid="new-bank-btn" className="bg-[#002FA7] text-white text-sm px-4 py-2.5 flex items-center gap-2 hover:bg-[#00227A] transition-colors duration-150"><Plus size={16}/> New Statement</button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 p-3 mb-4 grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="bank-filters">
        <div className="md:col-span-2 relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search bank, description, file…" data-testid="bank-search" className="w-full text-sm border border-zinc-200 pl-9 pr-3 py-2 focus:outline-none focus:border-[#002FA7]"/>
        </div>
        <select value={bankFilter} onChange={e=>setBankFilter(e.target.value)} data-testid="bank-filter-select" className="text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7] bg-white">
          <option value="">All banks</option>
          {banks.map(b => <option key={b.value} value={b.label}>{b.label}</option>)}
        </select>
        <input type="date" value={from} onChange={e=>setFrom(e.target.value)} data-testid="bank-from" className="font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]"/>
        <input type="date" value={to} onChange={e=>setTo(e.target.value)} data-testid="bank-to" className="font-mono text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]"/>
      </div>

      {(selected.size > 0 || hasF) && (
        <div className="mb-3 flex items-center gap-3 flex-wrap">
          {selected.size > 0 && (
            <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-200 px-3 py-1.5 text-sm" data-testid="bank-selection-bar">
              <span className="font-mono text-xs text-indigo-900">{selected.size} selected</span>
              <button onClick={bulkPreview} data-testid="bank-bulk-preview" className="inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline"><Eye size={12}/> Preview</button>
              <button onClick={onPrint} data-testid="bank-bulk-print" className="inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline"><Printer size={12}/> Print</button>
              <button onClick={shareLink} data-testid="bank-bulk-share" className="inline-flex items-center gap-1 text-xs text-[#002FA7] hover:underline"><Copy size={12}/> Share link</button>
              <button onClick={clearSelection} className="text-xs text-zinc-500 hover:underline">Clear</button>
            </div>
          )}
          {hasF && (
            <div className="text-xs text-zinc-500 font-mono flex items-center gap-3">
              <FunnelSimple size={12}/> Showing {displayed.length} of {items.length}
              <button onClick={clearF} className="text-[#002FA7] hover:underline">Clear filters</button>
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[920px]">
          <thead>
            <tr className="border-b border-zinc-200 text-[10px] uppercase font-mono text-zinc-500 bg-zinc-50">
              <th className="px-4 py-3 w-10">
                <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} data-testid="bank-select-all" className="accent-[#002FA7] cursor-pointer"/>
              </th>
              <SortTh label="Date"        k="date"        sortKey={sortKey} dir={sortDir} onClick={()=>toggleSort("date")}/>
              <SortTh label="Bank"        k="bank"        sortKey={sortKey} dir={sortDir} onClick={()=>toggleSort("bank")}/>
              <SortTh label="Description" k="description" sortKey={sortKey} dir={sortDir} onClick={()=>toggleSort("description")}/>
              <th className="text-left px-4 py-3">File</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center py-16 text-zinc-500 font-mono text-sm">Loading…</td></tr>
            : displayed.length === 0 ? <tr><td colSpan={6} className="text-center py-16 text-zinc-500"><Bank size={40} weight="duotone" className="mx-auto text-zinc-300 mb-3"/><div className="font-mono text-sm">{items.length===0?"No bank statements yet — add the first one.":"No statements match these filters."}</div></td></tr>
            : displayed.map(r => (
              <tr key={r.id} className={`border-b border-zinc-100 transition-colors duration-150 ${selected.has(r.id)?"bg-indigo-50/40":"hover:bg-zinc-50"}`} data-testid={`bank-row-${r.id}`}>
                <td className="px-4 py-3">
                  <input type="checkbox" checked={selected.has(r.id)} onChange={()=>toggleRow(r.id)} data-testid={`bank-select-${r.id}`} className="accent-[#002FA7] cursor-pointer"/>
                </td>
                <td className="px-4 py-3 font-mono text-zinc-700 whitespace-nowrap">{fmtDate(r.date)}</td>
                <td className="px-4 py-3 text-zinc-900 font-medium truncate max-w-[220px]">{r.bank_name}</td>
                <td className="px-4 py-3 text-zinc-700 truncate max-w-[320px]" title={r.description}>{r.description || <span className="text-zinc-400">—</span>}</td>
                <td className="px-4 py-3">
                  {r.filename ? (
                    <button onClick={()=>openFile(r.id)} className="inline-flex items-center gap-1 text-[#002FA7] hover:underline text-sm" data-testid={`bank-view-${r.id}`}>
                      <Paperclip size={14} weight="duotone"/> <span className="truncate max-w-[200px]">{r.filename}</span>
                    </button>
                  ) : <span className="text-zinc-400 font-mono text-xs">—</span>}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {r.filename && (<button onClick={()=>openFile(r.id)} data-testid={`bank-download-${r.id}`} className="inline-flex items-center gap-1 text-xs text-[#002FA7] px-2 py-1 hover:bg-blue-50 transition-colors duration-150"><DownloadSimple size={14}/> File</button>)}
                  <button onClick={()=>setEditing(r)} data-testid={`bank-edit-${r.id}`} className="inline-flex items-center gap-1 text-xs text-[#002FA7] px-2 py-1 hover:bg-blue-50 transition-colors duration-150 ml-1"><PencilSimple size={14}/> Edit</button>
                  {(user?.role === "admin" || r.created_by_id === user?.id) && (<button onClick={()=>del(r)} data-testid={`bank-delete-${r.id}`} className="inline-flex items-center gap-1 text-xs text-red-700 px-2 py-1 hover:bg-red-50 transition-colors duration-150 ml-1"><TrashSimple size={14}/> Delete</button>)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && <NewBankModal passcode={passcode} banks={banks} onBanksChange={setBanks} onClose={()=>setShowNew(false)} onCreated={rs=>{ setItems(xs=>[...rs,...xs]); setShowNew(false); }}/>}
      {editing && <EditBankModal passcode={passcode} banks={banks} onBanksChange={setBanks} record={editing} onClose={()=>setEditing(null)} onSaved={u=>{ setItems(xs=>xs.map(x=>x.id===u.id?{...x,...u}:x)); setEditing(null); }}/>}
    </Layout>
  );
}

function SortTh({ label, k, sortKey, dir, onClick }) {
  const active = sortKey === k;
  const arrow = !active ? "↕" : dir === "asc" ? "↑" : "↓";
  return <th className="text-left px-4 py-3 select-none">
    <button type="button" onClick={onClick} data-testid={`sort-${k}`} className={`inline-flex items-center gap-1 uppercase tracking-[0.05em] hover:text-zinc-900 transition-colors duration-150 ${active?"text-zinc-900":"text-zinc-500"}`}>
      {label}<span className={`font-mono text-[11px] ${active?"text-[#002FA7]":"text-zinc-400"}`}>{arrow}</span>
    </button>
  </th>;
}

function BankNameSelect({ banks, value, onChange, onBanksChange, passcode, testidPrefix }) {
  const { user } = useAuth();
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!newLabel.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post("/bank-names", { label: newLabel.trim() }, { headers: { "X-Passcode": passcode }});
      const exists = banks.some(b => b.value === data.value);
      const nextBanks = exists ? banks : [...banks, data];
      onBanksChange(nextBanks);
      onChange(data.label);
      setNewLabel(""); setAdding(false);
      toast.success("Bank added");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="flex gap-2">
        <select value={value} onChange={e=>onChange(e.target.value)} data-testid={`${testidPrefix}-select`} className="flex-1 text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7] bg-white">
          <option value="">— select bank —</option>
          {banks.map(b => <option key={b.value} value={b.label}>{b.label}</option>)}
        </select>
        {user?.role === "admin" && (
          <button type="button" onClick={()=>setAdding(v=>!v)} data-testid={`${testidPrefix}-add-btn`} className="text-sm border border-zinc-200 bg-white px-3 py-2 hover:bg-zinc-100 inline-flex items-center gap-1" title="Add new bank">
            <Plus size={14}/>
          </button>
        )}
      </div>
      {adding && (
        <div className="mt-2 flex gap-2" data-testid={`${testidPrefix}-add-panel`}>
          <input value={newLabel} onChange={e=>setNewLabel(e.target.value)} placeholder="e.g. Bank Rakyat" data-testid={`${testidPrefix}-add-input`} className="flex-1 text-sm border border-zinc-200 px-3 py-2 focus:outline-none focus:border-[#002FA7]"/>
          <button type="button" onClick={submit} disabled={saving || !newLabel.trim()} data-testid={`${testidPrefix}-add-save`} className="text-sm bg-[#002FA7] text-white px-3 py-2 hover:bg-[#00227A] disabled:opacity-50">{saving?"Saving…":"Save"}</button>
        </div>
      )}
    </div>
  );
}

function NewBankModal({ passcode, banks, onBanksChange, onClose, onCreated }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [bankName, setBankName] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    if (!bankName.trim()) { toast.error("Please select a bank"); return; }
    setSaving(true);
    try {
      const created = [];
      const rounds = files.length || 1;
      for (let i = 0; i < rounds; i++) {
        const fd = new FormData();
        fd.append("date", date);
        fd.append("bank_name", bankName.trim());
        fd.append("description", description.trim());
        if (files[i]) fd.append("file", files[i]);
        const { data } = await api.post("/bank-statements", fd, { headers: { "Content-Type": "multipart/form-data", "X-Passcode": passcode }});
        created.push(data);
      }
      toast.success(created.length === 1 ? "Statement added" : `${created.length} statements added`);
      onCreated(created);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return <Modal title="New Bank Statement" testid="new-bank-modal" onClose={onClose}>
    <form onSubmit={submit} className="p-5 space-y-4">
      <Fld label="Bank Name" required>
        <BankNameSelect banks={banks} value={bankName} onChange={setBankName} onBanksChange={onBanksChange} passcode={passcode} testidPrefix="bank-new-bank"/>
      </Fld>
      <Fld label="Date" required><input type="date" value={date} onChange={e=>setDate(e.target.value)} data-testid="bank-date-input" className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/></Fld>
      <Fld label="Description"><textarea rows={2} value={description} onChange={e=>setDescription(e.target.value)} data-testid="bank-desc-input" className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
      <Fld label="Attachments (any file type — creates one statement per file)">
        <MultiFilePicker files={files} onChange={setFiles} testid="bank-file" hint="PDF, Excel, image, ZIP…"/>
      </Fld>
      <ModalFooter onClose={onClose} saving={saving} submitTestId="bank-submit-btn"/>
    </form>
  </Modal>;
}

function EditBankModal({ passcode, banks, onBanksChange, record, onClose, onSaved }) {
  const [date, setDate] = useState(record.date || "");
  const [bankName, setBankName] = useState(record.bank_name || "");
  const [description, setDescription] = useState(record.description || "");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch(`/bank-statements/${record.id}`, {
        date, bank_name: bankName.trim(), description: description.trim(),
      }, { headers: { "X-Passcode": passcode }});
      toast.success("Saved"); onSaved(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return <Modal title="Edit Bank Statement" testid="edit-bank-modal" onClose={onClose}>
    <form onSubmit={submit} className="p-5 space-y-4">
      <Fld label="Bank Name"><BankNameSelect banks={banks} value={bankName} onChange={setBankName} onBanksChange={onBanksChange} passcode={passcode} testidPrefix="bank-edit-bank"/></Fld>
      <Fld label="Date"><input type="date" value={date} onChange={e=>setDate(e.target.value)} data-testid="bank-edit-date" className="w-full font-mono text-sm border border-zinc-200 px-3 py-2"/></Fld>
      <Fld label="Description"><textarea rows={2} value={description} onChange={e=>setDescription(e.target.value)} data-testid="bank-edit-desc" className="w-full text-sm border border-zinc-200 px-3 py-2"/></Fld>
      <ModalFooter onClose={onClose} saving={saving} submitTestId="bank-edit-save"/>
    </form>
  </Modal>;
}
